import { useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import Toggle from "@/ui/Toggle";
import { useToast } from "@/ui/Toast";
import SeccionLayout, { BloqueSeccion, PieGuardar } from "../SeccionLayout";
import { AYUDA_SECCION } from "@/components/ayuda/vistas/configuracion";
import { useAppSettings, useBorradorSeccion } from "../useAppSettings";

/**
 * Revisión automática de los tickets de Queclink (que atiende soporte con
 * Zendesk), las credenciales de nuestra cuenta, y los dos jobs diarios que
 * viven de esa cuenta: el índice de firmware (07:10) y la base de conocimiento
 * (07:40). Antes esos dos no tenían UI: se prendían/apagaban con un UPDATE.
 */
export default function IntegracionQueclink() {
  const toast = useToast();
  const { settings, cargando, guardando, guardar, alternar, recargar } = useAppSettings();
  const { valores, set, sucio } = useBorradorSeccion(settings, (s) => ({
    queclink_email: s.queclink_email,
  }));
  // Horas de los jobs diarios: su propio borrador para que «Guardar las horas»
  // no arrastre el correo de la cuenta (ni al revés).
  const horas = useBorradorSeccion(settings, (s) => ({
    queclink_fw_hora: s.queclink_fw_hora,
    queclink_kb_hora: s.queclink_kb_hora,
  }));
  // La contraseña no viaja de vuelta nunca, así que vive fuera del borrador:
  // vacía significa "conservar la que ya está".
  const [password, setPassword] = useState("");
  const [probando, setProbando] = useState(false);
  const [sincronizando, setSincronizando] = useState<"fw" | "kb" | null>(null);

  async function sincronizar(cual: "fw" | "kb") {
    setSincronizando(cual);
    try {
      if (cual === "fw") {
        const r = await api.post<{ cambio: boolean; equipos: number; versiones: number; nuevas: number }>(
          "/api/queclink/firmware/sincronizar",
        );
        toast.success(
          r.cambio ? "Índice de firmware actualizado" : "Índice de firmware sin cambios",
          `${r.equipos} equipos · ${r.versiones} versiones · ${r.nuevas} nuevas`,
        );
      } else {
        const r = await api.post<{ creadas: number; actualizadas: number; sin_cambios: number }>(
          "/api/queclink/kb/sincronizar",
        );
        toast.success(
          "Base de conocimiento sincronizada",
          `${r.creadas} nuevas · ${r.actualizadas} actualizadas · ${r.sin_cambios} sin cambios`,
        );
      }
      // Refresca «última sincronización» sin tocar nada.
      await recargar();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude sincronizar con Queclink", msg);
    } finally {
      setSincronizando(null);
    }
  }

  async function probar() {
    setProbando(true);
    try {
      const r = await api.get<{ usuario: string; tickets_abiertos: number }>(
        "/api/queclink/probar",
      );
      toast.success(
        `Conectado como ${r.usuario}`,
        `${r.tickets_abiertos} ticket(s) abierto(s) en Queclink`,
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude conectar con Queclink", msg);
    } finally {
      setProbando(false);
    }
  }

  async function guardarCuenta() {
    if (!valores) return;
    const ok = await guardar(
      password.trim() ? { ...valores, queclink_password: password.trim() } : valores,
      "Cuenta de Queclink guardada",
    );
    if (ok) setPassword("");
  }

  if (cargando || !settings || !valores || !horas.valores) {
    return <p className="text-sm text-zinc-500">Cargando…</p>;
  }
  const fmtUltimo = (iso: string) => (iso ? `última vez: ${iso}` : "todavía no ha corrido");

  return (
    <SeccionLayout
      ayuda={AYUDA_SECCION["queclink"]}
      ancho="formulario"
      titulo="Tickets de Queclink"
      descripcion="Queclink atiende soporte con Zendesk, así que ArkTickets se conecta con nuestra cuenta y hace lo mismo que con Jimi: trae lo que contesten a la bitácora del caso y te deja responderles desde ahí. El ticket se liga solo cuando su asunto trae el TE del caso. Como contestan en horario de México, se revisa cada hora entre las 7 de la mañana y las 8 de la noche, no una vez al día como Jimi, que contesta de madrugada."
    >
      <div className="card space-y-4">
        <BloqueSeccion titulo="Revisión automática">
          <Toggle
            checked={settings.queclink_revision_activa}
            onChange={(v) =>
              void alternar(
                "queclink_revision_activa",
                v,
                v ? "Revisión de Queclink encendida" : "Revisión de Queclink apagada",
              )
            }
            label="Revisar los tickets de Queclink cada hora"
            hint="Los tickets que Queclink ya cerró no se revisan: ya no cambian. Apagarlo no quita el botón del caso."
          />
        </BloqueSeccion>

        <BloqueSeccion
          titulo="Nuestra cuenta en Queclink"
          descripcion="La contraseña se guarda cifrada y nunca se vuelve a mostrar. Déjala vacía para conservar la que ya está."
        >
          <label className="block text-xs text-zinc-400">
            Correo de la cuenta
            <input
              value={valores.queclink_email}
              onChange={(e) => set({ queclink_email: e.target.value })}
              placeholder="tucorreo@ejemplo.com"
              className="input mt-1 w-full"
              autoComplete="off"
            />
          </label>
          <label className="block text-xs text-zinc-400">
            Contraseña
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={
                settings.queclink_password_guardada ? "•••••••• (ya guardada)" : "sin guardar"
              }
              className="input mt-1 w-full"
              autoComplete="new-password"
            />
          </label>
          <button
            type="button"
            onClick={() => void probar()}
            disabled={probando}
            className="btn-ghost w-full text-xs disabled:opacity-50 sm:w-auto"
          >
            {probando ? "Probando…" : "Probar la conexión"}
          </button>
          <PieGuardar
            sucio={sucio || password.trim().length > 0}
            guardando={guardando}
            onGuardar={() => void guardarCuenta()}
            textoGuardar="Guardar la cuenta"
          />
        </BloqueSeccion>

        <BloqueSeccion
          titulo="Lo que se trae de Queclink cada día"
          descripcion="Dos tareas diarias que usan la misma cuenta. Cada una se prende y apaga al instante; las horas se guardan con el botón. Van con media hora de diferencia a propósito para no pegarle dos veces seguidas al proveedor."
        >
          <div className="space-y-2">
            <Toggle
              checked={settings.queclink_fw_activo}
              onChange={(v) =>
                void alternar(
                  "queclink_fw_activo",
                  v,
                  v ? "Índice de firmware encendido" : "Índice de firmware apagado",
                )
              }
              label="Índice de firmware y descargas"
              hint="Regenera la guía con las versiones de firmware de cada equipo. Cuando sale una versión nueva, avisa solo al dueño."
            />
            <div className="flex flex-wrap items-center gap-2 pl-1 text-xs text-zinc-500">
              <label>
                Hora de Monterrey
                <input
                  type="time"
                  value={horas.valores.queclink_fw_hora}
                  onChange={(e) => horas.set({ queclink_fw_hora: e.target.value })}
                  disabled={!settings.queclink_fw_activo}
                  className="input ml-2 w-32 disabled:opacity-50"
                />
              </label>
              <span>· {fmtUltimo(settings.queclink_fw_ultimo)}</span>
              {settings.queclink_fw_guia_id && (
                <Link to={`/guias/${settings.queclink_fw_guia_id}`} className="text-accent-400 hover:underline">
                  ver la guía
                </Link>
              )}
              <button
                type="button"
                onClick={() => void sincronizar("fw")}
                disabled={sincronizando !== null}
                className="btn-ghost text-xs disabled:opacity-50"
              >
                {sincronizando === "fw" ? "Sincronizando…" : "↻ Sincronizar ahora"}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Toggle
              checked={settings.queclink_kb_activo}
              onChange={(v) =>
                void alternar(
                  "queclink_kb_activo",
                  v,
                  v ? "Base de conocimiento encendida" : "Base de conocimiento apagada",
                )
              }
              label="Base de conocimiento (sus artículos → Guías)"
              hint="Trae los artículos de la ayuda de Queclink como guías en borrador y las mantiene al día."
            />
            <div className="flex flex-wrap items-center gap-2 pl-1 text-xs text-zinc-500">
              <label>
                Hora de Monterrey
                <input
                  type="time"
                  value={horas.valores.queclink_kb_hora}
                  onChange={(e) => horas.set({ queclink_kb_hora: e.target.value })}
                  disabled={!settings.queclink_kb_activo}
                  className="input ml-2 w-32 disabled:opacity-50"
                />
              </label>
              <span>· {fmtUltimo(settings.queclink_kb_ultimo)}</span>
              <button
                type="button"
                onClick={() => void sincronizar("kb")}
                disabled={sincronizando !== null}
                className="btn-ghost text-xs disabled:opacity-50"
              >
                {sincronizando === "kb" ? "Sincronizando…" : "↻ Sincronizar ahora"}
              </button>
            </div>
          </div>

          <PieGuardar
            sucio={horas.sucio}
            guardando={guardando}
            onGuardar={() => void guardar(horas.valores!, "Horas guardadas")}
            textoGuardar="Guardar las horas"
          />
        </BloqueSeccion>
      </div>
    </SeccionLayout>
  );
}
